import moment from "moment"
import { DATE_KEY_FORMAT, LISTING_TYPES, REASON_TYPES, SLOT_DISPLAY_FORMAT, SLOT_VALUE_FORMAT, VISIBLE_SLOT_COUNT } from "./constants"

/**
 * The visible month grid: whole weeks, Sunday to Saturday, so the first and last rows spill into
 * the neighbouring months. Same window the request asks for, which is why the endpoint takes the
 * grid's bounds rather than the month's.
 */
export const buildMonthGrid = (monthDate) => {
  const start = moment(monthDate).startOf("month").day(0)
  const end = moment(monthDate).endOf("month").day(6)

  const weeks = []
  const cursor = start.clone()
  while (cursor.isSameOrBefore(end, "day")) {
    const week = []
    for (let index = 0; index < 7; index += 1) {
      week.push({ dateKey: cursor.format(DATE_KEY_FORMAT), isCurrentMonth: cursor.isSame(monthDate, "month") })
      cursor.add(1, "day")
    }
    weeks.push(week)
  }
  return { weeks, start: start.format(DATE_KEY_FORMAT), end: end.format(DATE_KEY_FORMAT) }
}

/** "10:00" -> "10:00 AM". */
export const formatSlotTime = (time) => moment(time, SLOT_VALUE_FORMAT).format(SLOT_DISPLAY_FORMAT)

/**
 * The collapsed slot line: the first few times joined, plus how many are left over.
 *
 * Takes the slots rather than the day, so the caller decides which ones count — the day's
 * `slots_total` is its whole schedule, while "+N more" counts only what is still on offer.
 */
export const summariseSlots = (slots = [], visibleCount = VISIBLE_SLOT_COUNT) => {
  const visible = slots.slice(0, visibleCount)
  return {
    times: visible.map((slot) => formatSlotTime(slot.time)),
    remainingCount: Math.max(slots.length - visible.length, 0)
  }
}

/**
 * The slots a day should actually show.
 *
 * `is_available: false` means the slot cannot be booked, and the two listing kinds want opposite
 * things from that. A ticketed slot keeps its place — "Fully Booked (12/12)" is the answer to
 * "can I still sell this?", and dropping it would leave the day looking unscheduled. A private
 * slot has no capacity to report, so an unbookable one says nothing and is left out rather than
 * listed among times a guide could offer a client.
 */
export const getBookableSlots = (day, isTicketed) => {
  const slots = day?.slots || []
  return isTicketed ? slots : slots.filter((slot) => slot.is_available !== false)
}

/** "09:00 AM · 1 spot left" — ticketed listings state capacity per slot (§3). */
export const formatTicketedSlot = (slot, t) => {
  const time = formatSlotTime(slot.time)
  // A ticketed listing whose slot arrives without capacity still has a usable start time.
  if (slot.spots_left == null && slot.capacity == null) return time
  if (slot.spots_left === 0) {
    return t("availability.slot_fully_booked", {
      defaultValue: "{{time}} · Fully Booked ({{booked}}/{{capacity}})",
      time,
      booked: slot.booked ?? slot.capacity,
      capacity: slot.capacity
    })
  }
  return t("availability.slot_spots_left", { defaultValue: "{{time}} · {{count}} spot left", time, count: slot.spots_left })
}

/**
 * The listing's type as a bare word — "private_trip" -> "private".
 *
 * The feed names the type the way the API models it, with a `_trip` suffix, while everything on
 * this page reasons about the bare kind. Normalising in one place keeps the comparison below and
 * the type pill's icon and label reading the same value.
 */
export const normalizeListingType = (listing) =>
  String(listing?.listing_type || "")
    .toLowerCase()
    .replace(/_trips?$/, "")

export const isTicketedListing = (listing) => normalizeListingType(listing) === LISTING_TYPES.TICKETED

/**
 * True when a reason renders as a solid scope pill rather than the white conflict chip.
 *
 * Blackouts and crew-off are statements the guide made; the shared-resource reasons are
 * consequences of scheduling. The design gives the two groups different chips.
 */
export const isBlackoutReason = (reason) => reason?.type === REASON_TYPES.BLACKOUT || reason?.type === REASON_TYPES.CREW_OFF

/**
 * A day's reasons with one entry per pill.
 *
 * The feed sends one reason per blackout record, so a day two equipment blackouts cover carries two
 * "Equipments Blackout" reasons. Each still gets its own card in the day panel; as a pill it would
 * just say the same thing twice.
 */
export const getChipReasons = (reasons) => {
  const seen = new Set()
  return (reasons || []).filter((reason) => {
    const key = [reason?.type, reason?.scope, reason?.label].join("|")
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Splits a day's reasons into the two groups the day panel renders as separate sections.
 *
 * The split is the same one `isBlackoutReason` draws in the grid: a blackout is something the
 * guide declared and gets the full record card, a conflict is a consequence of scheduling and
 * gets a one-line card naming what is taken and where.
 */
export const groupReasons = (reasons = []) =>
  reasons.reduce(
    (groups, reason) => {
      if (isBlackoutReason(reason)) groups.blackouts.push(reason)
      else groups.conflicts.push(reason)
      return groups
    },
    { blackouts: [], conflicts: [] }
  )

/**
 * "06:00 AM - 09:00 AM" for a reservation row.
 *
 * The booking feed has sent times as both `HH:mm:ss` and `HH:mm`, so both are parsed; anything
 * else is shown as it arrived rather than as "Invalid date".
 */
export const formatBookingTimeRange = (booking) => {
  const read = (value) => {
    if (!value) return ""
    const parsed = moment(value, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"], true)
    return parsed.isValid() ? parsed.format(SLOT_DISPLAY_FORMAT) : value
  }
  const start = read(booking?.start_time)
  const end = read(booking?.end_time)
  return [start, end].filter(Boolean).join(" - ")
}
